import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { cities, IAuthRegister, IAuthVerified } from '../../../types';
import { AuthService } from '../../../auth.service';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatError, MatHint } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter'
import { MatDatepickerModule } from '@angular/material/datepicker';
import { NgxCountriesDropdownModule } from 'ngx-countries-dropdown';
import { NgTemplateOutlet } from '@angular/common';
import { DateAdapter } from '@angular/material/core';
import { PaintDirective } from '../../../paint.directive';
import { backend } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { from, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'auth-register',
  templateUrl: './auth-register.component.html',
  styleUrls: ['./auth-register.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideLuxonDateAdapter()
  ],
  imports: [MatIcon, MatButton, MatIconButton, ReactiveFormsModule, MatHint, MatError, MatSelectModule, MatDatepickerModule, NgxCountriesDropdownModule, NgTemplateOutlet, PaintDirective]
})
export class AuthRegisterComponent implements OnInit {

  auth_id = input<string>();
  
  state = output<'request'>();
  login = output<IAuthVerified>();
  
  loading = signal<boolean>(false);

  auth = inject(AuthService);
  http = inject(HttpClient);
  toast = inject(ToastrService);
  destroy = inject(DestroyRef);

  luxonDateAdapter = inject(DateAdapter);

  cities = cities;

  selectedCity = signal<{ name: string; country: string; actual?: string; }|null>(null);

  form = new FormGroup({
    auth_id: new FormControl<string>('', [Validators.required]),
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    name: new FormControl<string>('', [Validators.required]),
    surname: new FormControl<string>('', [Validators.required]),
    birthday: new FormControl<string>('', [Validators.required]),
    gender: new FormControl<string>('', [Validators.required]),
    tourist: new FormControl<boolean>(false, [Validators.required]),
    country: new FormControl<string|null>('Malta', [Validators.required]),
    city: new FormControl<string|null>(null, [Validators.required])
  });

  dob = new FormControl<DateTime|null>(null, [Validators.required]);
  minDob: Date = DateTime.now().minus({ years: 120 }).toJSDate()
  maxDob: Date = DateTime.now().minus({ years: 16 }).startOf('day').toJSDate();

  constructor() {
    this.luxonDateAdapter.getFirstDayOfWeek = () => 1;

    this.form.get('tourist')!.valueChanges.subscribe(tourist => {
      if(tourist){
        this.form.get('city')!.disable();
        if(this.form.get('country')!.value == 'Malta'){
          this.form.get('country')!.setValue(null);
        }
      } else {
        this.form.get('city')!.enable();
        this.form.get('country')!.setValue('Malta');
      }
    });
    
    this.form.get('city')!.valueChanges.subscribe(city => {
      this.selectedCity.set(this.cities.find(c => (c.actual||c.name) == city)||null);
    });

    this.dob.valueChanges.subscribe(date => {
      if(date){
        this.form.get('birthday')!.setValue(date.toUTC(0,{ keepLocalTime: true }).toFormat('yyyy-MM-dd'));
      } else {
        this.form.get('birthday')!.setValue('');
      }
    });
   }

  ngOnInit(): void {
    const auth_id = this.auth_id();
    if(auth_id){
      this.form.get('auth_id')!.setValue(auth_id);
    }
  }

  createAccount(){

    if(!this.form.valid){
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);

    const userRegister = this.form.value as IAuthRegister;

    from(this.auth.getIP()).pipe(switchMap(ip => this.http.post<IAuthVerified>(backend + 'auth/register', userRegister, ip ? { headers: { cuip: ip } } : undefined)), takeUntilDestroyed(this.destroy)).subscribe({
      next: response => {
        this.login.emit(response);
      },
      error: error => {
        error = (error.error || error);

        this.loading.set(false);
        switch(error.code){
          case 5: {
            this.form.get('dob')!.setErrors({ age: true });
            break;
          }
          case 6:
          case 7: {
            this.toast.error('Your phone authorization expired or is not valid. Please verify your phone number again');
            this.state.emit('request');
            return;
          }
          case 16: {
            this.toast.info('Your account has already been created. Please validate your phone number again to login');
            this.state.emit('request');
            return;
          }
          case 19:
          case 20: {
            this.form.get('email')!.setErrors({ bad: true });
            break;
          }
        }
        this.toast.error(error.message || 'Unknown error occurred');
      }
    })

  }

}
